import React, { useState } from 'react'
import { aiService } from '@templatean/shared/services'
import { outlookService } from '../services/outlookService'

interface EmailParameters {
  recipient: string
  purpose: string
  tone: 'formal' | 'casual' | 'friendly' | 'persuasive' | 'informative'
  length: 'short' | 'medium' | 'long'
  includeGreeting: boolean
  includeSignature: boolean
  additionalContext: string
}

const AIEmailComposer: React.FC = () => {
  const [parameters, setParameters] = useState<EmailParameters>({
    recipient: '',
    purpose: '',
    tone: 'formal',
    length: 'medium',
    includeGreeting: true,
    includeSignature: true,
    additionalContext: ''
  })
  const [generating, setGenerating] = useState(false)
  const [generatedEmail, setGeneratedEmail] = useState<{
    subject: string
    body: string
  } | null>(null)

  const handleGenerateEmail = async () => {
    if (!parameters.purpose) {
      alert('Please describe the purpose of your email')
      return
    }

    setGenerating(true)
    try {
      const prompt = `Generate a ${parameters.tone} email ${parameters.length === 'short' ? '(2-3 paragraphs)' : parameters.length === 'medium' ? '(4-5 paragraphs)' : '(6+ paragraphs)'} for the following purpose:

Purpose: ${parameters.purpose}
${parameters.recipient ? `Recipient: ${parameters.recipient}` : ''}
${parameters.additionalContext ? `Additional Context: ${parameters.additionalContext}` : ''}

Requirements:
- Tone: ${parameters.tone}
- ${parameters.includeGreeting ? 'Include appropriate greeting' : 'Skip greeting'}
- ${parameters.includeSignature ? 'Include professional signature placeholder' : 'Skip signature'}
- Provide both subject line and email body
- Format the response as JSON with "subject" and "body" fields`

      const result = await aiService.generateContent({ prompt })
      
      // Parse the AI response
      const emailContent = JSON.parse(result.content)
      setGeneratedEmail(emailContent)
    } catch (error) {
      console.error('Error generating email:', error)
      alert('Failed to generate email. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleUseEmail = async () => {
    if (!generatedEmail) return

    try {
      await outlookService.setSubject(generatedEmail.subject)
      await outlookService.insertHtml(generatedEmail.body, 'replace')
      alert('Email content applied successfully!')
    } catch (error) {
      console.error('Error applying email:', error)
      alert('Failed to apply email content')
    }
  }

  const handleRefineEmail = async (refinementType: 'shorter' | 'longer' | 'more-formal' | 'more-casual') => {
    if (!generatedEmail) return

    setGenerating(true)
    try {
      let refinementPrompt = ''
      switch (refinementType) {
        case 'shorter':
          refinementPrompt = 'Make this email more concise while keeping the main points:'
          break
        case 'longer':
          refinementPrompt = 'Expand this email with more detail and explanation:'
          break
        case 'more-formal':
          refinementPrompt = 'Make this email more formal and professional:'
          break
        case 'more-casual':
          refinementPrompt = 'Make this email more casual and friendly:'
          break
      }

      const prompt = `${refinementPrompt}

Subject: ${generatedEmail.subject}
Body: ${generatedEmail.body}

Provide the refined version as JSON with "subject" and "body" fields.`

      const result = await aiService.generateContent({ prompt })
      const refinedEmail = JSON.parse(result.content)
      setGeneratedEmail(refinedEmail)
    } catch (error) {
      console.error('Error refining email:', error)
      alert('Failed to refine email')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="ai-email-composer">
      <div className="composer-header">
        <h2>AI Email Composer</h2>
        <p>Let AI help you write the perfect email</p>
      </div>

      <div className="composer-form">
        <div className="form-group">
          <label>Recipient (optional)</label>
          <input
            type="text"
            placeholder="e.g., Manager, Client, Team"
            value={parameters.recipient}
            onChange={(e) => setParameters({ ...parameters, recipient: e.target.value })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Purpose of Email *</label>
          <textarea
            placeholder="e.g., Request time off for vacation next month, Follow up on project proposal, Thank colleague for their help"
            value={parameters.purpose}
            onChange={(e) => setParameters({ ...parameters, purpose: e.target.value })}
            className="form-textarea"
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tone</label>
            <select
              value={parameters.tone}
              onChange={(e) => setParameters({ ...parameters, tone: e.target.value as any })}
              className="form-select"
            >
              <option value="formal">Formal</option>
              <option value="casual">Casual</option>
              <option value="friendly">Friendly</option>
              <option value="persuasive">Persuasive</option>
              <option value="informative">Informative</option>
            </select>
          </div>

          <div className="form-group">
            <label>Length</label>
            <select
              value={parameters.length}
              onChange={(e) => setParameters({ ...parameters, length: e.target.value as any })}
              className="form-select"
            >
              <option value="short">Short (2-3 paragraphs)</option>
              <option value="medium">Medium (4-5 paragraphs)</option>
              <option value="long">Long (6+ paragraphs)</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Additional Context (optional)</label>
          <textarea
            placeholder="Any additional information that might help generate a better email..."
            value={parameters.additionalContext}
            onChange={(e) => setParameters({ ...parameters, additionalContext: e.target.value })}
            className="form-textarea"
            rows={2}
          />
        </div>

        <div className="form-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={parameters.includeGreeting}
              onChange={(e) => setParameters({ ...parameters, includeGreeting: e.target.checked })}
            />
            <span>Include greeting</span>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={parameters.includeSignature}
              onChange={(e) => setParameters({ ...parameters, includeSignature: e.target.checked })}
            />
            <span>Include signature</span>
          </label>
        </div>

        <button
          onClick={handleGenerateEmail}
          disabled={generating || !parameters.purpose}
          className="btn btn-primary generate-btn"
        >
          {generating ? 'Generating...' : 'Generate Email'}
        </button>
      </div>

      {generatedEmail && (
        <div className="generated-email">
          <div className="email-preview">
            <div className="preview-header">
              <h3>Generated Email</h3>
              <div className="preview-actions">
                <button onClick={handleUseEmail} className="btn btn-primary">
                  Use This Email
                </button>
              </div>
            </div>

            <div className="email-content">
              <div className="email-subject">
                <strong>Subject:</strong> {generatedEmail.subject}
              </div>
              <div className="email-body" dangerouslySetInnerHTML={{ __html: generatedEmail.body }} />
            </div>

            <div className="refinement-options">
              <h4>Refine Email</h4>
              <div className="refinement-buttons">
                <button 
                  onClick={() => handleRefineEmail('shorter')}
                  disabled={generating}
                  className="btn btn-secondary"
                >
                  Make Shorter
                </button>
                <button 
                  onClick={() => handleRefineEmail('longer')}
                  disabled={generating}
                  className="btn btn-secondary"
                >
                  Make Longer
                </button>
                <button 
                  onClick={() => handleRefineEmail('more-formal')}
                  disabled={generating}
                  className="btn btn-secondary"
                >
                  More Formal
                </button>
                <button 
                  onClick={() => handleRefineEmail('more-casual')}
                  disabled={generating}
                  className="btn btn-secondary"
                >
                  More Casual
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIEmailComposer